import moment from "moment";

/**
 * Classifies an array collection based on a property name
 * @param array An array containing other arrays
 * @param prop Property name to classify
 */
export function classifyByProperty(array: any[], prop: string) {
    return array.reduce((r, a) => {
        r[a[prop]] = r[a[prop]] || [];
        r[a[prop]].push(a);
        return r;
    }, {});
}

/**
 * Retrieves the value for a given cookie name
 * @param name Cookie name
 * @returns string | undefined
 */
export function getCookie(name) {
  const value = '; ' + document.cookie;
  const parts = value.split('; ' + name + '=');
  if (parts.length == 2) return parts.pop().split(';').shift()
}

/**
 * Convert HTML string to JSON format
 * @param data HTML String
 * @param element [lid] attribute of Cognos table
 */
export function htmlToJson(data, element): any[] {
  // Parse HTML
  const htmlDoc = new DOMParser().parseFromString(data, 'text/html');
  // Get table by lid
  const table = htmlDoc.querySelectorAll(element);
  const rows = [];
  // Iterate over each row
  for (let i = 0; i < table.length; i++) {
    const row = [];
    // Iterate over each field
    for (let t = 0; t < table[i].childNodes.length; t++) {
      // Check if field contains 1 or more spans
      if (table[i].childNodes[t].childNodes.length === 1) {
        row.push(table[i].childNodes[t].innerText);
      } else {
        row.push(table[i].childNodes[t].childNodes[table[i].childNodes[t].childNodes.length - 1].innerText);
      }
    }
    rows.push(row);
  }
  // Remove headers
  rows.shift();
  return rows;
}

/**
 * Retrieves a search URIComponent as a JSON object
 */
export function getJsonFromUrl() {
  const query = location.search.substr(1);
  const result = {};
  query.split('&').forEach(function (part) {
    const item = part.split('=');
    result[item[0]] = decodeURIComponent(item[1]);
  });
  return result;
}

/**
 * Retrieves the previous month
 * @param date moment.Moment
 */
export function GetPreviousMonth(date: string) {
  return moment(date, ['YYYY-MM-DD', 'YYYY-MM']).subtract(1, 'years').format('YYYY-MM');
}

/**
 * Sums values of an array collection
 * @param array An array containing other arrays
 * @param i A number defining the index of the desired value to make the sum
 */
export function sumByIndex(array: any[], i: number): number {
    return array.reduce((a, b) => a + parseInt(b[i], 10), 0);
}

/**
 * Sums values of an array collection
 * @param array An array containing other arrays
 * @param prop Property name to use
 */
export function sumByProperty(array: any[], prop: string): number {
  return Math.round(array.reduce((a, b) => a + parseFloat(b[prop]), 0));
}

/**
 * Transform any number to humanized format
 * @param value Value in format of number, if passed as string, it will be parsed as int
 * @param htmlFormat Wether or not to return the result using HTML tags
 */
export function numeralFn(value: number | string, htmlFormat: boolean = true) {
    // Try to parse string number as number type
    if (typeof value === 'string') {
      // Parse value as a float, for decimal precision
      value = parseFloat(value);
    }
    // Check if it's negative
    const isNegative = value < 0;
    // Get absolute value
    value = Math.abs(value);
    let formatted = `${value}`;
    // Return as trillions (G), millions (M) or thousands (K)
    let symbol = '';
    const regexThousands = /\B(?=(\d{3})+(?!\d))/g;
    if (Math.abs(value) >= 1000) {
      formatted = (value / 1000).toFixed(0).replace(regexThousands, '.');
      symbol = 'K';
    }
    if (Math.abs(value) >= 1000000) {
      formatted = (value / 1000000).toFixed(0).replace(regexThousands, '.');
      symbol = 'M';
    }
    if (Math.abs(value) >= 1000000000) {
      formatted = (value / 1000000000).toFixed(0).replace(regexThousands, '.');
      symbol = 'G';
    }
    // Formatting
    if (htmlFormat) {
      return `
        <span class="numeral-value">${isNegative ? '- ' : ''}${formatted}</span>
        <span class="numeral-symbol">${symbol}</span>
      `;
    } else {
      return `${isNegative ? '- ' : ''}${formatted} ${symbol}`;
    }
}

/**
 * Converts a dotted format version to number
 */
export function versionToNumber(version: string): number {
  return parseInt(version.replace(/\./, ''), 10);
}


/**
 * Sanitizes a CSV text string
 * @param csv String of CSV file
 * @url https://stackoverflow.com/questions/53776683/regex-find-newline-between-double-quotes-and-replace-with-space
 */
export function sanitizeCSV(csv: string): string {
  // Remove new lines and carriage returns between double quotes
  csv = csv.replace(/"[^"]*"/g, (match, capture) => match.replace(/[\n\r]\s*/g, ''));
  return csv;
}

/**
 * Retrieves a list of valid delimiters for a given CSV string
 * @param csv String of CSV file
 * @param delimiters Array of possible delimiters
 * @param fallbackDelimiter Default delimiter to use if no other is found
 * @url https://www.elasticfeed.com/a1bd25ce92c8282f28bbaf96fef109bd/
 * @returns An array of possible delimiters
 */
export function guessCSVDelimiter(csv: string, delimiters: string[] = [';',',','\t'], fallbackDelimiter: string = '\t'): string {
  // Filter delimiters by validating function
  const validDelimiters = delimiters.filter(checkDelimiter);
  return validDelimiters.length > 0 ? validDelimiters[0] : fallbackDelimiter;
  function checkDelimiter(delimiter) {
    // Set default number of fields per line
    let cache = -1;
    // Split lines and make sure every line using current delimiter has the same length of fields
    return csv.split('\n').every(checkLength);
    function checkLength(line) {
      if (!line) return true;
      // Get number of fields in current line
      const length = line.split(delimiter).length;
      if (cache < 0) cache = length;
      // Check if final line field count is same as first line
      return cache === length && length > 1;
    }
  }
}

/**
 * Function to convert CSV string data to JSON Array data
 * @param csv string csv data
 * @param numeralFields array of indexes which should parsed as numeral
 * @param removeHeaders provide true to remove first line of headers, default: true
 */
export function csvToJson(csv: string, numeralFields: number[], removeHeaders: boolean = true) {
  csv = sanitizeCSV(csv);
  const delimiter = guessCSVDelimiter(csv);
  const lines: any[] = csv.split('\n');
  const data = [];

  // Remove Header Lines from CSV
  if (removeHeaders) {
    lines.splice(0, 1);
  }

  // Number of remaining lines
  const length = lines.length;
  let i = 0;

  // Loop over Archive and separate columns into data-array
  for ( ; i < length; i++ ) {
    // Remove empty lines
    if (lines[i].trim().length === 0) {
      continue;
    }
    const values = lines[i].split(delimiter);
    numeralFields.forEach(num => {
      values[num] = isNaN(values[num]) ? 0 : parseFloat(values[num]);
    });
    data.push(values);
  }
  return data;
}

/**
 * Function to convert CSV string data to JSON Array data with headers as keys
 * @param csv string csv data
 * @param numeralFields array of indexes which should parsed as numeral
 * @param removeHeaders provide true to remove first line of headers
 */
export function csvToJsonNamed(csv: string): any[] {
  csv = sanitizeCSV(csv);
  const delimiter = guessCSVDelimiter(csv);
  const rows = [];
  const lines: any[] = csv.split('\n');
  const headers = lines.shift().split(delimiter).map(el => el.trim());
  lines.forEach(line => {
    if (line.length > 0) {
      const newRow = {};
      line.split(delimiter).forEach((element, index) => {
        newRow[headers[index]] = element.trim();
      });
      rows.push(newRow);
    }
  });
  return rows;
}

/**
 * Function to check if line is CRLF, LF or CR
 * @param data 
 * @returns lineending or unknown
 */
export function guessLineEnding(data: any) {
       // Check if the text contains CRLF (\r\n)
       let lineending="unknown" 
       if (/\r\n/.test(data)) {
         lineending="\r\n"
       }
       // Check if the text contains LF (\n) only
       else if (/\n/.test(data)) {
         lineending="\n"
       }
       // Check if the text contains CR (\r) only
       else if (/\r/.test(data)) {
         lineending="\r"
       }
       // If none of the line endings are detected
       else {
         this.logger.info("Unknown line ending. Check data input.", "functions.js", "deliveriesToJSON()")
       }
       return lineending
  }

/**
 * Function to convert CSV string data to JSON Array data with headers as keys
 * @param data string txt data
 */
export function deliveriesToJson(data: any, language: string) {
    let deliveriesObject =  [];
    let header: any = [];

    // return empty array if there is no data
    if (!data) { 
      this.logger.info(`Returning empty array`,"functions.ts","deliveriesToJson()")
      return [];
    }
      
    try {
      // remove quotes
      // replace comma with a dot in order for angular to be able to interpret float and double values
      data = data.replace(/"/g, '').replace(/,/g, '.');

      // check string separator
      const delimiter = guessCSVDelimiter(data);

      // split text into lines
      // Check if the text contains CRLF (\r\n)
      const lineending = guessLineEnding(data)
      const lines = data.split(lineending)

      // removes the first item from array of lines which contains names of columns and saves it in variable header
      header = lines.shift().split(delimiter);

      var lineCnt = 0;

      // Loop oer remaining lines of data
      lines.forEach(line => {
        // return if line is empty
        if (line === lineending) return;

        lineCnt++;

        // removes the first \n from line and splits it be delimiter
        let splitLine = line.replace(/^\s+|\s+$/g, '').split(delimiter);

        // Check if there are members in splitLine, otherwise return, as it makes no sense to work on that line 
        if (splitLine.length<2) return

        let lineObject = {};


        for(let i=0; i<splitLine.length; i++) {
          // check if line frase is a number, if so convert it to number
          if(Number(splitLine[i])) splitLine[i] = Number(splitLine[i]);

          // creates a property in lineObject and sets its value as currently read string
          // the name of a property comes from header variable
          // ex: if we are currently reading value of splitLine[0], then header[0] returns adecuate description for this value, which in case of 0 is 'data set'.
          // and so on sequentially for any index, header[x] returns description(column name) for the data that splitLine[x] contains
          lineObject[header[i]] = splitLine[i];
        }

        // get plant name
        let plant = lineObject["Plant"];

        // get dataset name
        let dataset = lineObject["data set"];

        // throw error if 'Plant' and 'data set' column names are not present
        if(!plant || !dataset) throw "Unexpected property 'data set' or 'Plant' on line "+lineCnt+"";

          // check if there is an property with plants name in object, if not create it
          if(!deliveriesObject[plant]) deliveriesObject[plant] = {};

          if(dataset == 'day') {
            // if compilation enters in this if, means that currently read object represents report data of a certain day
            // ex: day	3/1/2022	3	0	2022	3/2022	Plant	#1	Plant #1	0	25	0	105	0	65	0	195	0	8	0	194	0	389	0

            // property of plant object(ex: 01/02/2022), which holds an array of delivery's data for that date
            const deliveryDate = delivery_date_to_dd_mm_yyyy( lineObject["Delivery Date"] );

            // check if property already exists, if not create it and define it as array
            if(!deliveriesObject[plant][deliveryDate]) deliveriesObject[plant][deliveryDate] = {}

            // push delivery data
            deliveriesObject[plant][deliveryDate] = lineObject;
        } else if (dataset == 'month') {
            // if compilation enters in this else, means that currently read object represents monthly average data
            // ex: month	4/8/2022	3	3	2022	3/2022	Plant	#1	Plant #1	23	3079	133	3783	164	3386	147	10248	445	431	18	3699	160	13947	606

            // check if property 'month' already exists in plant, if not create it and define it as array
            if(!deliveriesObject[plant]["month"]) deliveriesObject[plant]["month"] = []

            const dates = delivery_date_to_zoned_mmmm_yyyy( lineObject["Month Name"], language );

            lineObject["month_id"] = dates["MMMM_YYYY"];
            lineObject["Month"] = dates["mm"]

            // push monthly average delivery data
            deliveriesObject[plant]["month"].push(lineObject);
        }
      })
      return deliveriesObject;
    }
    catch(error) {
      console.error("Catch Error", error)
      return []
    }
}

// recieves date string in format ex: 05(month)-09(day)-2021(year)
// returns date string in format ex: 05(day)/09(month)/2021(year)
export function delivery_date_to_dd_mm_yyyy(date: string): string {
  // throw error if date doesnt contain '-' or contains only one '-', valid date for Delivery Date column, the format should be 'year-month-day' separeted with a score
  // where month and day can be double or single digit number
  if(!date.includes('-') || (date.split('-').length -1) < 2  ) throw "invalid date format, for Delivery ";

  const [del_year, del_month, del_day] = date.split("-").map(item => Number(item));
  const newDate =  moment( new Date(del_year, del_month-1, del_day), "DD/MM/YYYY").format("DD/MM/YYYY");  
  return newDate;
}

// recieves date string in format ex: 05(month)/2021(year)
// returns date string in format ex: MAY-2021
export function delivery_date_to_zoned_mmmm_yyyy(date: string, language: string): Object {
  // throw error if date doesnt contain '/' or contains more than one '/', valid date for Month Name column, the format should be 'month/year' separeted with a slash
  // where month can be double or single digit number
  if(!date.includes('/') || (date.split('/').length -1) > 1 ) { throw "invalid date format: [" +date+ "] expected e.g. 2/2024" }

  const [month, year] = date.split("/").map(item => Number(item));
  const mmmm_yyyy = moment( new Date(year, month-1, 1), "DD/MM/YYYY", language).format("MMMM/YYYY");
  const mm = moment( new Date(year, month-1, 1), "DD/MM/YYYY", language).format("MM");
  return  {
    MMMM_YYYY: mmmm_yyyy,
    mm: mm
  };
}


/**
 * Format a dot separated version as v?
 * */
export function formatVersion(string): string {
  return 'v' + string.replace(/[^0-9.]/g, '');
}

// Get current year
export function getYear(): number {
  return (new Date()).getFullYear();
}

/* Returns a random integer between min (inclusive) and max (inclusive)
* Using Math.round() will give you a non-uniform distribution!
*/
export function getRandomInt(min, max) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

/* Return a truthy number */
export function removeCommas(x: string): number {
  return +x.toString().replace(/,|./g, '');
}

/* Return a percent number with/without sign */
export function percent(part: number, total: number, sign?: boolean, space_between?: boolean, zeroSign?: boolean): number | string {
  sign = sign || false;
  space_between = space_between || false;
  zeroSign = zeroSign || false;
  if (zeroSign && total === 0) {
    return '-';
  }
  if (sign) {
    return parseInt(((part * 100) / total).toFixed(0), 10) + (space_between ? ' ' : '') + '%';
  } else {
    return parseInt(((part * 100) / total).toFixed(0), 10);
  }
}

/**
 * Retrieves a date formatted as DD/MM/YYYY using multiple parse formats
 * @param text String of date
 * @param moment Moment plugin object
 * @param firstMonth Wether of not date contains month as first value in format
 */
export function getPlanDateWithMoment(text: string, moment, firstMonth: boolean = false): string {
  if (firstMonth) {
    if (text.lastIndexOf('.') > -1) {
      return moment(text, 'MM.DD.YYYY').format('DD/MM/YYYY');
    } else if (text.indexOf('/') > -1) {
      return moment(text, 'MM/DD/YYYY').format('DD/MM/YYYY');
    } else if (text.indexOf('-') > -1) {
      return moment(text, 'YYYY-MM-DD').format('DD/MM/YYYY');
    } else if (text.indexOf(',') > -1) {
      return moment(text, 'MMM DD, YYYY').format('DD/MM/YYYY');
    } else {
      return moment(text, 'YYYYMMDD').format('DD/MM/YYYY');
    }
  } else {
    if (text.lastIndexOf('.') > -1) {
      return moment(text, 'DD.MM.YYYY').format('DD/MM/YYYY');
    } else if (text.indexOf('/') > -1) {
      return moment(text, 'DD/MM/YYYY').format('DD/MM/YYYY');
    } else if (text.indexOf('-') > -1) {
      return moment(text, 'YYYY-MM-DD').format('DD/MM/YYYY');
    } else if (text.indexOf(',') > -1) {
      return moment(text, 'MMM DD, YYYY').format('DD/MM/YYYY');
    } else {
      return moment(text, 'YYYYMMDD').format('DD/MM/YYYY');
    }
  }
}

export function objectHasProperties(object) {
  return Object.keys(object).length > 0 ? true : false
}


export function errorCatcher(message: string, error: Error) {
  console.error("=================== ERROR")
  console.error("=================== ERROR")
  console.error("=================== ERROR")
  console.error(message,error);
  console.error("=================== ERROR")
  console.error("=================== ERROR")
  console.error("=================== ERROR")
}